import React from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  TextField,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import DeleteIcon from "@material-ui/icons/Delete";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import { getFileUrl } from "../../../../common/tools";
import { reorder } from "./gallery-editor.view";

/**
 * Danh sách tài liệu pháp lý của dự án. Thứ tự trên giao diện = sortOrder khi lưu.
 * items: [{ key, id, fileId, file, name, documentType, documentNumber, issuedDate, issuedBy, isPublic }]
 * file: { fileId, fileName, filePreview } | null (null = file đã bị xoá khỏi Quản lý tài liệu)
 * errors: { [index]: { [field]: message } }
 */
const DocumentEditor = ({ items, errors = {}, onChange }) => {
  const updateItem = (index, field, value) => {
    onChange(items.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    onChange(reorder(items, result.source.index, result.destination.index));
  };

  if (items.length === 0) {
    return <p className="text-muted mb-0">Chưa có tài liệu nào.</p>;
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="investment-documents">
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            {items.map((item, index) => {
              const file = item.file;
              const rowErrors = errors[index] || {};
              return (
                <Draggable key={item.key} draggableId={item.key} index={index}>
                  {(dragProvided, snapshot) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      className="d-flex align-items-start border rounded p-2 mb-2 bg-white"
                      style={{
                        ...dragProvided.draggableProps.style,
                        boxShadow: snapshot.isDragging ? "0 4px 12px rgba(0,0,0,.15)" : "none",
                      }}
                    >
                      <span {...dragProvided.dragHandleProps} className="mr-2 mt-3 text-muted" title="Kéo để sắp xếp">
                        <DragIndicatorIcon />
                      </span>
                      <div className="flex-grow-1">
                        <div className="mb-1">
                          <InsertDriveFileIcon fontSize="small" className="mr-1 text-muted" />
                          {file && file.filePreview ? (
                            <a href={getFileUrl(file.filePreview)} target="_blank" rel="noopener noreferrer">
                              {file.fileName}
                            </a>
                          ) : (
                            <small className="text-danger">File #{item.fileId} không tồn tại</small>
                          )}
                          {rowErrors.file && (
                            <span className="error d-block">{rowErrors.file}</span>
                          )}
                        </div>
                        <div className="row">
                          <div className="col-md-6 mb-2">
                            <TextField
                              fullWidth
                              size="small"
                              label="Tên tài liệu *"
                              value={item.name}
                              onChange={(e) => updateItem(index, "name", e.target.value)}
                              inputProps={{ maxLength: 255 }}
                              error={!!rowErrors.name}
                              helperText={rowErrors.name}
                            />
                          </div>
                          <div className="col-md-3 mb-2">
                            <TextField
                              fullWidth
                              size="small"
                              label="Loại văn bản"
                              placeholder="Quyết định, Công văn..."
                              value={item.documentType}
                              onChange={(e) => updateItem(index, "documentType", e.target.value)}
                              inputProps={{ maxLength: 100 }}
                            />
                          </div>
                          <div className="col-md-3 mb-2">
                            <TextField
                              fullWidth
                              size="small"
                              label="Số hiệu"
                              value={item.documentNumber}
                              onChange={(e) => updateItem(index, "documentNumber", e.target.value)}
                              inputProps={{ maxLength: 100 }}
                            />
                          </div>
                          <div className="col-md-3 mb-2">
                            <TextField
                              fullWidth
                              size="small"
                              type="date"
                              label="Ngày ban hành"
                              InputLabelProps={{ shrink: true }}
                              value={item.issuedDate}
                              onChange={(e) => updateItem(index, "issuedDate", e.target.value)}
                            />
                          </div>
                          <div className="col-md-6 mb-2">
                            <TextField
                              fullWidth
                              size="small"
                              label="Cơ quan ban hành"
                              value={item.issuedBy}
                              onChange={(e) => updateItem(index, "issuedBy", e.target.value)}
                              inputProps={{ maxLength: 255 }}
                            />
                          </div>
                          <div className="col-md-3 mb-2 d-flex align-items-end">
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={item.isPublic}
                                  onChange={(e) => updateItem(index, "isPublic", e.target.checked)}
                                  color="primary"
                                />
                              }
                              label="Công khai"
                            />
                          </div>
                        </div>
                      </div>
                      <Tooltip title="Bỏ tài liệu">
                        <IconButton aria-label="delete" onClick={() => removeItem(index)}>
                          <DeleteIcon className="text-danger" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default DocumentEditor;
